import { useEffect, useState } from 'react';

const loadImage = (src) => {
    return new Promise((resolve) => {
        if (!src) return resolve();

        const img = new Image();
        const done = () => resolve();

        img.onload = done;
        img.onerror = done;
        img.src = src;
    });
};

const loadVideo = (src) => {
    return new Promise((resolve) => {
        if (!src) return resolve();

        const video = document.createElement('video');
        const done = () => {
            video.onloadeddata = null;
            video.onerror = null;
            resolve();
        };

        video.preload = 'auto';
        video.onloadeddata = done;
        video.onerror = done;
        video.src = src;
    });
};

/**
 * Preload a list of asset URLs (images / videos) and expose loading progress.
 * This is used to drive the global splash/loading screen.
 */
const useAssetPreloader = (urls, options = {}) => {
    const { minimumDuration = 2000 } = options;
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const assets = Array.isArray(urls) ? urls.filter(Boolean) : [];

        if (assets.length === 0) {
            setProgress(100);
            return;
        }

        let cancelled = false;
        const startTime = performance.now();
        let loadedCount = 0;

        const updateProgress = () => {
            if (cancelled) return;
            const baseProgress = (loadedCount / assets.length) * 100;
            setProgress((prev) => (baseProgress > prev ? baseProgress : prev));
        };

        const loaders = assets.map((url) => {
            const isVideo = /\.(mp4|webm|ogg)$/i.test(url);
            const loader = isVideo ? loadVideo : loadImage;

            return loader(url).then(() => {
                loadedCount += 1;
                updateProgress();
            });
        });

        Promise.allSettled(loaders).then(() => {
            if (cancelled) return;

            const elapsed = performance.now() - startTime;
            const remaining = Math.max(0, minimumDuration - elapsed);

            setTimeout(() => {
                if (cancelled) return;
                setProgress(100);
            }, remaining);
        });

        return () => {
            cancelled = true;
        };
    }, [urls, minimumDuration]);

    return {
        progress: Math.min(100, progress),
        isComplete: progress >= 100
    };
};

export default useAssetPreloader;

